import { useState } from 'react';
import { Download, Film, History, Image as ImageIcon, Link2, Loader2, Play, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { historyMediaUrl, isHistoryVideoEntry } from '@/lib/mascotAnimation';

const KIND_LABEL = {
  seedance2: 'Seedance 2',
  local_webm: 'Transparent WebM',
  local_mp4: 'Animation MP4',
  character_sheet: 'Character sheet',
  sheet_green: 'Green screen frame',
  sheet_cutout: 'Cutout frame',
  reference_photo: 'Reference photo',
  styled_reference: 'Cartoon reference',
};

function statusColor(status) {
  if (status === 'completed') return '#10B981';
  if (status === 'failed') return '#EF4444';
  if (status === 'running' || status === 'pending') return '#0EA5E9';
  return 'var(--text-tertiary)';
}

export function MascotVideoHistoryPanel({
  history = [],
  backendUrl,
  selectedId,
  onSelect,
  onRefresh,
  loading = false,
  onImportUrl,
  importing = false,
  onRetrySeedance,
  retryingId = '',
}) {
  const rows = Array.isArray(history) ? history : [];
  const [importUrl, setImportUrl] = useState('');

  return (
    <aside
      className="w-full lg:w-80 shrink-0 flex flex-col border-l border-[var(--glass-border)] bg-[var(--bg-secondary)]/60 min-h-0"
      data-testid="mascot-video-history-panel"
    >
      <div className="p-4 border-b border-[var(--glass-border)] flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <History className="w-4 h-4 text-sky-500 shrink-0" />
          <h3 className="font-semibold text-sm truncate">Export history</h3>
        </div>
        {onRefresh ? (
          <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={onRefresh} disabled={loading}>
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Refresh'}
          </Button>
        ) : null}
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-2 min-h-[12rem] max-h-[calc(100vh-12rem)]">
        {!rows.length ? (
          <p className="text-xs text-[var(--text-tertiary)] px-1 py-6 text-center leading-relaxed">
            Exported videos appear here — Seedance 2 MP4 and transparent WebM clips.
          </p>
        ) : (
          rows.map((row) => {
            const mediaUrl = historyMediaUrl(row, backendUrl);
            const active = selectedId === row.id;
            const isVideo = row.status === 'completed' && isHistoryVideoEntry(row) && mediaUrl;
            const isImage = row.status === 'completed' && !isHistoryVideoEntry(row) && mediaUrl;
            const isBusy = row.status === 'running' || row.status === 'pending';
            const downloadUrl = row.video_url ? historyMediaUrl({ ...row, media_url: row.video_url }, backendUrl) : mediaUrl;
            return (
              <button
                key={row.id}
                type="button"
                onClick={() => onSelect?.(row)}
                className="w-full text-left rounded-xl border p-2.5 transition-colors"
                style={{
                  borderColor: active ? '#0EA5E9' : 'var(--glass-border)',
                  background: active ? 'rgba(14,165,233,0.08)' : 'var(--bg-primary)',
                }}
              >
                <div className="flex gap-2">
                  <div
                    className="w-16 h-10 rounded-md overflow-hidden shrink-0 flex items-center justify-center"
                    style={{
                      background: row.kind === 'local_webm' || row.kind === 'sheet_cutout'
                        ? 'repeating-conic-gradient(#334155 0% 25%, #1e293b 0% 50%)'
                        : '#0f172a',
                      backgroundSize: row.kind === 'local_webm' || row.kind === 'sheet_cutout' ? '8px 8px' : undefined,
                    }}
                  >
                    {isVideo ? (
                      <video src={mediaUrl} className="w-full h-full object-cover" muted playsInline />
                    ) : isImage ? (
                      <img src={mediaUrl} alt="" className="w-full h-full object-cover" />
                    ) : isBusy ? (
                      <Loader2 className="w-4 h-4 animate-spin text-sky-400" />
                    ) : (
                      <Film className="w-4 h-4 text-slate-500" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium truncate">{row.preset_label || row.preset_id || row.kind}</p>
                    <p className="text-[10px] text-[var(--text-tertiary)] truncate">
                      {KIND_LABEL[row.kind] || row.kind}
                      {row.character_names?.length ? ` · ${row.character_names.join(' & ')}` : ''}
                      {row.shot_count ? ` · ${row.shot_count} shots` : ''}
                    </p>
                    <p className="text-[10px] font-medium mt-0.5" style={{ color: statusColor(row.status) }}>
                      {row.status}
                      {row.duration_sec ? ` · ${row.duration_sec}s` : ''}
                    </p>
                  </div>
                </div>
                {row.error ? (
                  <p className="text-[10px] text-red-500 mt-1 line-clamp-2">{row.error}</p>
                ) : null}
                {row.status === 'failed' && row.kind === 'seedance2' && onRetrySeedance ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 text-[10px] w-full mt-2"
                    disabled={Boolean(retryingId)}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRetrySeedance(row);
                    }}
                  >
                    {retryingId === row.id ? (
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    ) : (
                      <RefreshCw className="w-3 h-3 mr-1" />
                    )}
                    Retry Seedance 2
                  </Button>
                ) : null}
                {(isVideo || isImage) && downloadUrl ? (
                  <div className="flex gap-1 mt-2">
                    {isVideo ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7 text-[10px] flex-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelect?.(row);
                        }}
                      >
                        <Play className="w-3 h-3 mr-1" />
                        Preview
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7 text-[10px] flex-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelect?.(row);
                        }}
                      >
                        <ImageIcon className="w-3 h-3 mr-1" />
                        View
                      </Button>
                    )}
                    <Button type="button" variant="outline" size="sm" className="h-7 px-2" asChild>
                      <a href={downloadUrl} download target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                        <Download className="w-3 h-3" />
                      </a>
                    </Button>
                  </div>
                ) : null}
              </button>
            );
          })
        )}
      </div>

      {onImportUrl ? (
        <div className="p-3 border-t border-[var(--glass-border)] space-y-2">
          <p className="text-[10px] font-medium text-[var(--text-secondary)]">Import Runway / video URL</p>
          <Input
            value={importUrl}
            onChange={(e) => setImportUrl(e.target.value)}
            placeholder="Paste Seedance CloudFront or MP4 link…"
            className="h-8 text-xs"
            data-testid="mascot-import-video-url"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full h-8 text-xs"
            disabled={importing || !importUrl.trim()}
            onClick={() => {
              const url = importUrl.trim();
              if (!url) return;
              onImportUrl(url);
              setImportUrl('');
            }}
          >
            {importing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Link2 className="w-3 h-3 mr-1" />}
            Add to history
          </Button>
        </div>
      ) : null}
    </aside>
  );
}
